import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, IconButton, Snackbar, Stack, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DataTable from "components/common/tables/DataTable";
import { usePageTitle } from "context/PageTitleContext";
import enterpriseService from "../../services/personnel/enterprise.service";
import { userService } from "../../services/user.service";
import {
  catalogApi,
  normalizeDepartment,
  normalizeDepartmentUnit,
  normalizeResponseError,
} from "../../services/catalogs/systemCatalog.service";
import CatalogFormDialog from "./CatalogFormDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import { AppButton } from "components/common/buttons";

const normalizeEnterprise = (item) => ({
  value: item?.enterpriseId ?? item?.EnterpriseId,
  label: item?.enterpriseName ?? item?.EnterpriseName ?? item?.name ?? item?.Name ?? "",
});

const normalizeLeaderOption = (item) => {
  const value = item?.userId ?? item?.UserId;
  const fullName = item?.fullName ?? item?.FullName ?? "";
  const userName = item?.userName ?? item?.UserName ?? "";

  if (!value) return null;

  return {
    value,
    label: fullName || userName || `User ${value}`,
  };
};

const withEmptyLeaderOption = (options) => [{ value: "", label: "Không chọn" }, ...options];

const createUnitFields = (leaderOptions) => [
  { name: "departmentUnitCode", label: "Mã đội" },
  { name: "departmentUnitName", label: "Tên đội", required: true },
  { name: "leaderUserId", label: "Trưởng đội", type: "select", options: withEmptyLeaderOption(leaderOptions) },
  { name: "phone", label: "Số điện thoại" },
  { name: "email", label: "Email" },
  { name: "description", label: "Mô tả", multiline: true },
];

const DepartmentUnitsPanel = ({ department, leaderOptions, onSnackbar }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({ open: false, mode: "create", row: null });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadUnits = useCallback(async () => {
    setLoading(true);
    try {
      const data = await catalogApi.getDepartmentUnits(department.departmentId);
      setRows(data.map(normalizeDepartmentUnit));
    } catch (error) {
      setRows([]);
      onSnackbar(normalizeResponseError(error, "Không thể tải danh sách đội"), "error");
    } finally {
      setLoading(false);
    }
  }, [department.departmentId, onSnackbar]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        departmentId: department.departmentId,
        departmentUnitCode: String(values.departmentUnitCode || "").trim() || null,
        departmentUnitName: String(values.departmentUnitName || "").trim(),
        leaderUserId: values.leaderUserId ? Number(values.leaderUserId) : null,
        phone: String(values.phone || "").trim() || null,
        email: String(values.email || "").trim() || null,
        description: String(values.description || "").trim() || null,
      };

      if (formState.mode === "edit") {
        await catalogApi.updateDepartmentUnit(formState.row.departmentUnitId, payload);
        onSnackbar("Cập nhật đội thành công");
      } else {
        await catalogApi.createDepartmentUnit(payload);
        onSnackbar("Thêm đội thành công");
      }

      setFormState({ open: false, mode: "create", row: null });
      await loadUnits();
    } catch (error) {
      onSnackbar(normalizeResponseError(error, "Không thể lưu đội"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await catalogApi.deleteDepartmentUnit(deleteTarget.departmentUnitId);
      setDeleteTarget(null);
      onSnackbar("Xóa đội thành công");
      await loadUnits();
    } catch (error) {
      onSnackbar(normalizeResponseError(error, "Không thể xóa đội"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "departmentUnitCode", header: "Mã đội", size: 120, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "departmentUnitName", header: "Tên đội", size: 240 },
      { accessorKey: "leaderFullName", header: "Trưởng đội", size: 160, Cell: ({ row }) => row.original.leaderFullName || row.original.leaderUserName || "-" },
      { accessorKey: "phone", header: "Số điện thoại", size: 120, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "email", header: "Email", size: 180, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "description", header: "Mô tả", size: 280, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
    []
  );

  return (
    <Box sx={{ p: 1.5, backgroundColor: "#f8fafc", borderTop: "1px solid #e5edf5" }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
          <Chip label={`Đội thuộc ${department.departmentName}`} size="small" sx={{ alignSelf: { xs: "flex-start", sm: "center" } }} />
        </Stack>
        <DataTable
          columns={columns}
          data={rows}
          enableRowActions
          enablePagination={false}
          enableBottomToolbar={false}
          maxTableHeight="320px"
          state={{ isLoading: loading, showProgressBars: submitting }}
          renderTopToolbarExtraActions={() => (
              <AppButton
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setFormState({ open: true, mode: "create", row: null })}
              >
                Thêm đội
              </AppButton>
          )}
          renderRowActions={({ row }) => (
            <Stack direction="row" spacing={0.5} justifyContent="center">
              <Tooltip title="Sửa">
                <IconButton size="small" color="primary" onClick={() => setFormState({ open: true, mode: "edit", row: row.original })}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Xóa">
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(row.original)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        />
      </Stack>

      <CatalogFormDialog
        open={formState.open}
        title={formState.mode === "edit" ? "Cập nhật đội" : "Thêm đội"}
        fields={createUnitFields(leaderOptions)}
        initialValues={formState.row}
        submitting={submitting}
        onClose={() => setFormState({ open: false, mode: "create", row: null })}
        onSubmit={handleSubmit}
      />
      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        message="Bạn có chắc chắn muốn xóa đội này?"
        submitting={submitting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
};

const DepartmentsManagement = () => {
  const { setPageTitle } = usePageTitle();
  const [rows, setRows] = useState([]);
  const [enterpriseOptions, setEnterpriseOptions] = useState([]);
  const [leaderOptions, setLeaderOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({ open: false, mode: "create", row: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [departments, enterprisesResponse, usersResponse] = await Promise.all([
        catalogApi.list("/Department"),
        enterpriseService.getAll(),
        userService.getAllUsers(),
      ]);

      setRows(departments.map(normalizeDepartment));

      const enterpriseRows = Array.isArray(enterprisesResponse?.data?.data)
        ? enterprisesResponse.data.data
        : Array.isArray(enterprisesResponse?.data)
          ? enterprisesResponse.data
          : [];
      setEnterpriseOptions(enterpriseRows.map(normalizeEnterprise).filter((item) => item.value));

      const userRows = Array.isArray(usersResponse?.data) ? usersResponse.data : [];
      setLeaderOptions(userRows.map(normalizeLeaderOption).filter(Boolean));
    } catch (error) {
      setRows([]);
      showSnackbar(normalizeResponseError(error, "Không thể tải danh sách phòng ban"), "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    setPageTitle("Quản lý phòng ban");
  }, [setPageTitle]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const fields = useMemo(
    () => [
      { name: "enterpriseId", label: "Xí nghiệp", required: true, type: "select", options: enterpriseOptions },
      { name: "departmentCode", label: "Mã phòng ban", required: true },
      { name: "departmentName", label: "Tên phòng ban", required: true },
      { name: "leaderUserId", label: "Trưởng phòng", type: "select", options: withEmptyLeaderOption(leaderOptions) },
      { name: "phone", label: "Số điện thoại" },
      { name: "email", label: "Email" },
      { name: "description", label: "Mô tả", multiline: true },
    ],
    [enterpriseOptions, leaderOptions]
  );

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        enterpriseId: Number(values.enterpriseId),
        departmentCode: String(values.departmentCode || "").trim(),
        departmentName: String(values.departmentName || "").trim(),
        leaderUserId: values.leaderUserId ? Number(values.leaderUserId) : null,
        phone: String(values.phone || "").trim() || null,
        email: String(values.email || "").trim() || null,
        description: String(values.description || "").trim() || null,
      };

      if (formState.mode === "edit") {
        await catalogApi.update("/Department", formState.row.departmentId, payload);
        showSnackbar("Cập nhật phòng ban thành công");
      } else {
        await catalogApi.create("/Department", payload);
        showSnackbar("Thêm phòng ban thành công");
      }

      setFormState({ open: false, mode: "create", row: null });
      await loadData();
    } catch (error) {
      showSnackbar(normalizeResponseError(error, "Không thể lưu phòng ban"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await catalogApi.remove("/Department", deleteTarget.departmentId);
      setDeleteTarget(null);
      showSnackbar("Xóa phòng ban thành công");
      await loadData();
    } catch (error) {
      showSnackbar(normalizeResponseError(error, "Không thể xóa phòng ban"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "departmentCode", header: "Mã phòng ban", size: 90 },
      { accessorKey: "departmentName", header: "Tên phòng ban", size: 160 },
      { accessorKey: "leaderFullName", header: "Trưởng phòng", size: 160, Cell: ({ row }) => row.original.leaderFullName || row.original.leaderUserName || "-" },
      { accessorKey: "enterpriseName", header: "Xí nghiệp", size: 180, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "phone", header: "Số điện thoại", size: 90, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "email", header: "Email", size: 170, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "description", header: "Mô tả", size: 300, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
    []
  );

  return (
    <Box sx={{ p: 2, backgroundColor: "#f9fafb", minHeight: "calc(100vh - 64px)" }}>
      <Stack spacing={2}>
        <DataTable
          columns={columns}
          data={rows}
          enableRowActions
          enableExpanding
          renderDetailPanel={({ row }) => <DepartmentUnitsPanel department={row.original} leaderOptions={leaderOptions} onSnackbar={showSnackbar} />}
          state={{ isLoading: loading, showProgressBars: submitting }}
          renderTopToolbarExtraActions={() => (
              // <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setFormState({ open: true, mode: "create", row: null })} disableElevation>
              //   Thêm phòng ban
              // </Button>
              <AppButton variant="contained" startIcon={<AddIcon />} onClick={() => setFormState({ open: true, mode: "create", row: null })} disableElevation>
                Thêm phòng ban
              </AppButton>
          )}
          renderRowActions={({ row }) => (
            <Stack direction="row" spacing={0.5} justifyContent="center">
              <Tooltip title="Sửa">
                <IconButton size="small" color="primary" onClick={() => setFormState({ open: true, mode: "edit", row: row.original })}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Xóa">
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(row.original)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        />
      </Stack>

      <CatalogFormDialog
        open={formState.open}
        title={formState.mode === "edit" ? "Cập nhật phòng ban" : "Thêm phòng ban"}
        fields={fields}
        initialValues={formState.row}
        submitting={submitting}
        onClose={() => setFormState({ open: false, mode: "create", row: null })}
        onSubmit={handleSubmit}
      />
      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        message="Bạn có chắc chắn muốn xóa phòng ban này?"
        submitting={submitting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DepartmentsManagement;
